import { CSSProperties, useMemo } from 'react';

import { BorderRadiusThemeProps } from 'shared/common/themes/theme';
import { useContentLanguage } from 'shared/components/providers/ContentLanguageProvider';
import { Direction } from 'shared/utils/locale.utils';
import { rem } from 'shared/utils/rem';
import { applyUnit, getThemeBorderStyleAsOneString } from 'shared/utils/theme.utils';
import { useGlobalTheme } from 'shared/utils/useGlobalTheme';

import classes from './RichText.module.scss';
import { DEFAULT_BACKGROUND_COLOR, DEFAULT_BORDER, DEFAULT_BORDER_RADIUS, DEFAULT_PADDING } from './consts';
import {
  BlockTitleTypographyStyle,
  BlockTitleWidgetStyles,
  TextWidgetStyles,
  TypographyType,
  WidgetDefaultStyles,
  WidgetTypographyDefaultStyle,
  WidgetTypographyStyle
} from '../../../utils/typographyStyles';

type RichTextStyles = {
  expandHeight?: boolean;
  widgetStyles?: TextWidgetStyles | BlockTitleWidgetStyles;
  widgetDefaultStyles?: WidgetDefaultStyles;
};

const getBorderStyles = (
  widgetDefaultStyles: WidgetTypographyDefaultStyle,
  widgetStyles: Partial<WidgetTypographyStyle>,
  contentDirection: Direction = 'ltr'
) => {
  const { borderTop, borderBottom, borderLeft, borderRight } = widgetStyles || {};

  const resultBorderRight = getThemeBorderStyleAsOneString(
    widgetDefaultStyles?.borderRight ?? DEFAULT_BORDER,
    undefined,
    borderRight || {}
  );
  const resultBorderLeft = getThemeBorderStyleAsOneString(
    widgetDefaultStyles?.borderLeft ?? DEFAULT_BORDER,
    undefined,
    borderLeft || {}
  );

  return {
    borderTop: getThemeBorderStyleAsOneString(
      widgetDefaultStyles?.borderTop ?? DEFAULT_BORDER,
      undefined,
      borderTop || {}
    ),
    borderRight: contentDirection === 'ltr' ? resultBorderRight : resultBorderLeft,
    borderBottom: getThemeBorderStyleAsOneString(
      widgetDefaultStyles?.borderBottom ?? DEFAULT_BORDER,
      undefined,
      borderBottom || {}
    ),
    borderLeft: contentDirection === 'ltr' ? resultBorderLeft : resultBorderRight
  };
};

const getSinglePadding = (widgetStyles?: Partial<WidgetTypographyStyle>) =>
  rem(widgetStyles?.padding ?? DEFAULT_PADDING);

const getMultiPadding = (
  widgetStyles: BlockTitleTypographyStyle,
  widgetDefaultStyles: WidgetTypographyDefaultStyle,
  contentDirection: Direction
) => {
  const {
    paddingTop: defaultPaddingTop,
    paddingRight: defaultPaddingRight,
    paddingBottom: defaultPaddingBottom,
    paddingLeft: defaultPaddingLeft
  } = widgetDefaultStyles || {};
  const { paddingTop, paddingRight, paddingBottom, paddingLeft } = widgetStyles || {};
  const isLTR = contentDirection === 'ltr';

  const top = applyUnit(paddingTop ?? defaultPaddingTop ?? 0);
  const right = applyUnit(paddingRight ?? defaultPaddingRight ?? 0);
  const bottom = applyUnit(paddingBottom ?? defaultPaddingBottom ?? 0);
  const left = applyUnit(paddingLeft ?? defaultPaddingLeft ?? 0);

  return `${top} ${isLTR ? right : left} ${bottom} ${isLTR ? left : right}`;
};

const getBorderRadiusIndividual = (
  borderRadius: Partial<BorderRadiusThemeProps> | BorderRadiusThemeProps,
  overrides?: Partial<BorderRadiusThemeProps>,
  direction: Direction = 'ltr'
) => {
  const { topLeft = 0, topRight = 0, bottomLeft = 0, bottomRight = 0, measureUnit = 'px' } = borderRadius;
  const effectiveTopLeft = overrides?.topLeft ?? topLeft;
  const effectiveTopRight = overrides?.topRight ?? topRight;
  const effectiveBottomLeft = overrides?.bottomLeft ?? bottomLeft;
  const effectiveBottomRight = overrides?.bottomRight ?? bottomRight;
  const effectiveMeasureUnit = overrides?.measureUnit ?? measureUnit;

  const resultTopLeft = direction === 'rtl' ? effectiveTopRight : effectiveTopLeft;
  const resultTopRight = direction === 'rtl' ? effectiveTopLeft : effectiveTopRight;
  const resultBottomLeft = direction === 'rtl' ? effectiveBottomRight : effectiveBottomLeft;
  const resultBottomRight = direction === 'rtl' ? effectiveBottomLeft : effectiveBottomRight;

  return {
    topLeft: applyUnit(resultTopLeft, effectiveMeasureUnit),
    topRight: applyUnit(resultTopRight, effectiveMeasureUnit),
    bottomLeft: applyUnit(resultBottomLeft, effectiveMeasureUnit),
    bottomRight: applyUnit(resultBottomRight, effectiveMeasureUnit)
  };
};

export const useRichTextStyles = (props: RichTextStyles) => {
  const { expandHeight, widgetStyles, widgetDefaultStyles } = props;
  const { contentDirection } = useContentLanguage();
  const defaultAlignment = contentDirection === 'ltr' ? 'left' : 'right';
  const theme = useGlobalTheme();

  const style: CSSProperties = useMemo(() => {
    const getBorderForTypographyType = (typographyType: TypographyType) => {
      return getBorderStyles(
        (widgetDefaultStyles?.[typographyType] as WidgetTypographyDefaultStyle) ?? {},
        widgetStyles?.[typographyType]?.style ?? {},
        contentDirection
      );
    };

    const getBorderRadiusForTypographyType = (typographyType: TypographyType) => {
      return getBorderRadiusIndividual(
        (widgetDefaultStyles?.[typographyType] as WidgetTypographyDefaultStyle)?.borderRadius ?? DEFAULT_BORDER_RADIUS,
        widgetStyles?.[typographyType]?.style?.borderRadius ?? {},
        contentDirection
      );
    };

    const getPaddingForTypographyType = (typographyType: TypographyType) => {
      return widgetDefaultStyles &&
        'paddingLeft' in widgetDefaultStyles[typographyType] &&
        typographyType !== TypographyType.BodyText
        ? getMultiPadding(
            widgetStyles?.[typographyType].style as BlockTitleTypographyStyle,
            widgetDefaultStyles?.[typographyType],
            contentDirection
          )
        : getSinglePadding((widgetStyles as TextWidgetStyles)?.[typographyType].style);
    };

    const titleBorders = getBorderForTypographyType(TypographyType.Title);
    const headlineBorders = getBorderForTypographyType(TypographyType.Headline);
    const subHeadlineBorders = getBorderForTypographyType(TypographyType.SubHeadline);
    const bodyTextBorders = getBorderForTypographyType(TypographyType.BodyText);

    const titleBorderRadius = getBorderRadiusForTypographyType(TypographyType.Title);
    const headlineBorderRadius = getBorderRadiusForTypographyType(TypographyType.Headline);
    const subHeadlineBorderRadius = getBorderRadiusForTypographyType(TypographyType.SubHeadline);
    const bodyTextBorderRadius = getBorderRadiusForTypographyType(TypographyType.BodyText);

    const titlePadding = getPaddingForTypographyType(TypographyType.Title);
    const headlinePadding = getPaddingForTypographyType(TypographyType.Headline);
    const subHeadlinePadding = getPaddingForTypographyType(TypographyType.SubHeadline);
    const bodyTextPadding = getPaddingForTypographyType(TypographyType.BodyText);

    const fontsTheme = theme.other.fonts;

    return {
      '--rich-text-default-alignment': defaultAlignment,
      '--min-question-height': expandHeight
        ? `calc(${theme.other.stage.minQuestionInputHeight} - ${theme.other.stage.questionInputWrapperPaddingSum})`
        : 'auto',
      // Title styles
      '--title-font-family': widgetStyles?.title?.style?.fontFamily ?? fontsTheme.title.fontFamily,
      '--title-font-size': rem(widgetDefaultStyles?.title?.fontSize ?? fontsTheme.title.fontSize),
      '--title-font-weight': fontsTheme.title.fontWeight.toString(),
      '--title-letter-spacing': rem(fontsTheme.title.letterSpacing),
      '--title-line-height': (widgetStyles?.title?.style?.lineHeight ?? fontsTheme.title.lineHeight).toString(),
      '--title-color': widgetStyles?.title?.style?.color ?? widgetDefaultStyles?.title?.color ?? fontsTheme.title.color,
      '--title-background':
        widgetStyles?.title?.style?.backgroundColor ??
        widgetDefaultStyles?.title?.backgroundColor ??
        DEFAULT_BACKGROUND_COLOR,
      '--title-padding': titlePadding,
      '--title-border-top': titleBorders.borderTop,
      '--title-border-right': titleBorders.borderRight,
      '--title-border-bottom': titleBorders.borderBottom,
      '--title-border-left': titleBorders.borderLeft,
      '--title-border-radius-top-left': titleBorderRadius.topLeft,
      '--title-border-radius-top-right': titleBorderRadius.topRight,
      '--title-border-radius-bottom-left': titleBorderRadius.bottomLeft,
      '--title-border-radius-bottom-right': titleBorderRadius.bottomRight,
      ...(widgetDefaultStyles?.title?.width && { '--title-width': widgetDefaultStyles.title.width }),

      // Headline styles
      '--headline-font-family': widgetStyles?.headline?.style?.fontFamily ?? fontsTheme.headline.fontFamily,
      '--headline-font-size': rem(widgetDefaultStyles?.title?.fontSize ?? fontsTheme.headline.fontSize),
      '--headline-font-weight': fontsTheme.headline.fontWeight.toString(),
      '--headline-letter-spacing': rem(fontsTheme.headline.letterSpacing),
      '--headline-line-height': (
        widgetStyles?.headline?.style?.lineHeight ?? fontsTheme.headline.lineHeight
      ).toString(),
      '--headline-color':
        widgetStyles?.headline?.style?.color ?? widgetDefaultStyles?.headline?.color ?? fontsTheme.headline.color,
      '--headline-background':
        widgetStyles?.headline?.style?.backgroundColor ??
        widgetDefaultStyles?.headline?.backgroundColor ??
        DEFAULT_BACKGROUND_COLOR,
      '--headline-padding': headlinePadding,
      '--headline-border-top': headlineBorders.borderTop,
      '--headline-border-right': headlineBorders.borderRight,
      '--headline-border-bottom': headlineBorders.borderBottom,
      '--headline-border-left': headlineBorders.borderLeft,
      '--headline-border-radius-top-left': headlineBorderRadius.topLeft,
      '--headline-border-radius-top-right': headlineBorderRadius.topRight,
      '--headline-border-radius-bottom-left': headlineBorderRadius.bottomLeft,
      '--headline-border-radius-bottom-right': headlineBorderRadius.bottomRight,
      ...(widgetDefaultStyles?.headline?.width && { '--headline-width': widgetDefaultStyles.headline.width }),

      // SubHeadline styles
      '--sub-headline-font-family': widgetStyles?.subHeadline?.style?.fontFamily ?? fontsTheme.subHeadline.fontFamily,
      '--sub-headline-font-size': rem(widgetDefaultStyles?.title?.fontSize ?? fontsTheme.subHeadline.fontSize),
      '--sub-headline-font-weight': fontsTheme.subHeadline.fontWeight.toString(),
      '--sub-headline-letter-spacing': rem(fontsTheme.subHeadline.letterSpacing),
      '--sub-headline-line-height': (
        widgetStyles?.subHeadline?.style?.lineHeight ?? fontsTheme.subHeadline.lineHeight
      ).toString(),
      '--sub-headline-color':
        widgetStyles?.subHeadline?.style?.color ??
        widgetDefaultStyles?.subHeadline?.color ??
        fontsTheme.subHeadline.color,
      '--sub-headline-background':
        widgetStyles?.subHeadline?.style?.backgroundColor ??
        widgetDefaultStyles?.subHeadline?.backgroundColor ??
        DEFAULT_BACKGROUND_COLOR,
      '--sub-headline-padding': subHeadlinePadding,
      '--sub-headline-border-top': subHeadlineBorders.borderTop,
      '--sub-headline-border-right': subHeadlineBorders.borderRight,
      '--sub-headline-border-bottom': subHeadlineBorders.borderBottom,
      '--sub-headline-border-left': subHeadlineBorders.borderLeft,
      '--sub-headline-border-radius-top-left': subHeadlineBorderRadius.topLeft,
      '--sub-headline-border-radius-top-right': subHeadlineBorderRadius.topRight,
      '--sub-headline-border-radius-bottom-left': subHeadlineBorderRadius.bottomLeft,
      '--sub-headline-border-radius-bottom-right': subHeadlineBorderRadius.bottomRight,
      ...(widgetDefaultStyles?.subHeadline?.width && { '--sub-headline-width': widgetDefaultStyles.subHeadline.width }),

      // BodyText styles
      '--body-text-font-family': widgetStyles?.bodyText?.style?.fontFamily ?? fontsTheme.bodyText.fontFamily,
      '--body-text-font-size': rem(widgetDefaultStyles?.title?.fontSize ?? fontsTheme.bodyText.fontSize),
      '--body-text-font-weight': fontsTheme.bodyText.fontWeight.toString(),
      '--body-text-letter-spacing': rem(fontsTheme.bodyText.letterSpacing),
      '--body-text-line-height': (
        widgetStyles?.bodyText?.style?.lineHeight ?? fontsTheme.bodyText.lineHeight
      ).toString(),
      '--body-text-color':
        widgetStyles?.bodyText?.style?.color ?? widgetDefaultStyles?.bodyText?.color ?? fontsTheme.bodyText.color,
      '--body-text-background':
        widgetStyles?.bodyText?.style?.backgroundColor ??
        widgetDefaultStyles?.bodyText?.backgroundColor ??
        DEFAULT_BACKGROUND_COLOR,
      '--body-text-padding': bodyTextPadding,
      '--body-text-border-top': bodyTextBorders.borderTop,
      '--body-text-border-right': bodyTextBorders.borderRight,
      '--body-text-border-bottom': bodyTextBorders.borderBottom,
      '--body-text-border-left': bodyTextBorders.borderLeft,
      '--body-text-border-radius-top-left': bodyTextBorderRadius.topLeft,
      '--body-text-border-radius-top-right': bodyTextBorderRadius.topRight,
      '--body-text-border-radius-bottom-left': bodyTextBorderRadius.bottomLeft,
      '--body-text-border-radius-bottom-right': bodyTextBorderRadius.bottomRight,
      ...(widgetDefaultStyles?.bodyText?.width && { '--body-text-width': widgetDefaultStyles.bodyText.width })
    } as CSSProperties;
  }, [contentDirection, defaultAlignment, expandHeight, theme, widgetDefaultStyles, widgetStyles]);

  return { classes, style };
};
